'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
} from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import storage from 'local-storage-fallback';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useReactivateProviderCohortMutation } from 'src/api/redux/providerCohorts';
import type { AccountFolder } from 'src/api/redux/accountFolders';
import { useSavedVolumePartitions } from 'src/components/CodeSetSelector/hooks/useSavedVolumePartitions';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { EncountersActionTypes } from 'src/context/reducers/encountersReducer';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import { useVolumeDragAndDrop } from 'src/components/CodeSetSelector/hooks/useVolumeDragAndDrop';
import { CODE_SET_KIND_SUBTITLES } from 'src/components/CodeSetSelector/constants';
import { MANAGE_VOLUME_ACTIVE_TAB_KEY } from './constants';
import { updateAccountFolder } from 'src/api/accountFolders/accountFoldersApi';
import { Input } from 'src/components/PulseUI';
import {
  faFolderPlus,
  faPencil,
  faTrashCanXmark,
  faFolderOpen,
  faFolderBookmark,
  faChevronDown,
  faChevronRight,
  faXmark,
  faCheck,
  faArrowRotateLeft,
} from '@fortawesome/pro-solid-svg-icons';
import { DeleteCodeSetDialog } from '../DeleteCodeSetDialog';
import { DeleteEncounterDialog } from '../DeleteEncounterDialog';
import { CreateFolderDialog } from '../CreateFolderDialog';
import { DeleteFolderDialog } from '../DeleteFolderDialog';
import { DeleteAISearchDialog } from '../DeleteAISearchDialog';
import { RenameAISearchDialog } from '../RenameAISearchDialog';
import { IconButton } from 'src/components/PulseUI/IconButton';

// Add type for combined volume items
interface VolumeItem {
  id: number;
  label: string;
  volumeType: 'cohort' | 'filterSet' | 'advancedSearch';
  status?: string;
}

interface TabProps {
  isActive: boolean;
  label: string;
  onClick: () => void;
}

const Tab: React.FC<TabProps> = ({ isActive, label, onClick }) => {
  return (
    <Box
      className={`flex-1 px-6 py-3 cursor-pointer relative text-center`}
      onClick={onClick}
      data-testid={`manage-volume-${label.toLowerCase()}-tab`}
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
    >
      <Typography
        className={`text-base ${
          isActive ? 'font-semibold text-text-primary' : 'text-neutral-600'
        }`}
      >
        {label}
      </Typography>
      {isActive && (
        <Box
          className="absolute bottom-0 left-0 right-0 h-1 bg-sky-600 rounded-t-lg"
          data-testid={`manage-volume-${label.toLowerCase()}-tab-indicator`}
        />
      )}
    </Box>
  );
};

interface ManageVolumeDrawerProps {
  /** Saved-volume location whose applied volume this drawer keeps in sync. */
  location: string;
}

const ManageVolumeDrawer = ({
  location,
}: ManageVolumeDrawerProps): React.ReactElement => {
  const { isDrawerOpen, closeDrawer, openDrawer } = useDrawer();
  const { dispatch } = useEncounters();

  const { setNotification } = useNotification();
  const log = useLogging();
  const [activeTab, setActiveTab] = useState<'PERSONAL' | 'COMPANY'>(
    'PERSONAL'
  );
  const [isDeleteEncounterDialogOpen, setIsDeleteEncounterDialogOpen] =
    useState(false);
  const [isDeleteFilterSetDialogOpen, setIsDeleteFilterSetDialogOpen] =
    useState(false);
  const [isDeleteAISearchDialogOpen, setIsDeleteAISearchDialogOpen] =
    useState(false);
  const [volumeToRename, setVolumeToRename] = useState<VolumeItem | null>(
    null
  );
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] =
    useState(false);
  const [isDeleteFolderDialogOpen, setIsDeleteFolderDialogOpen] =
    useState(false);
  const [createFolderScope, setCreateFolderScope] = useState<
    'PERSONAL' | 'COMPANY' | null
  >(null);
  const [volumeToDelete, setVolumeToDelete] = useState<VolumeItem | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<AccountFolder | null>(
    null
  );

  // Folder editing state
  const [editingFolderId, setEditingFolderId] = useState<number | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');

  // Folder state management (transient, resets on drawer close)
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});

  // Create a custom drawer lock mechanism
  const drawerLockRef = useRef(false);

  // Same partitioned content as the Discovery saved-volumes dropdown, so the
  // drawer lists exactly what the dropdown does (including AI searches).
  const {
    personalVolumes,
    companyVolumes,
    personalFolders,
    companyFolders,
    getFolderVolumes,
    isLoading,
    error: hasError,
    allCodeSets,
    cohortsData,
    advancedSearchesData,
    accountFoldersData,
    mutateAccountFolders,
    handleVolumeDeleted,
    renameAppliedAdvancedSearch,
  } = useSavedVolumePartitions(location);

  const {
    isDraggingRef,
    dragOverSection,
    setDragOverSection,
    handleDragStart,
    handleDragEnd,
    handleDrop: handleFolderDrop,
    handleSectionDrop,
    handleAccordionDragOver: handleSectionDragOver,
    handleAccordionDragLeave: handleSectionDragLeave,
  } = useVolumeDragAndDrop({
    cohortsData,
    allCodeSets,
    advancedSearchesData,
    accountFoldersData,
    mutateAccountFolders,
  });

  // Reactivate mutation (PR-1 endpoint); invalidates the cohort list on success.
  const [reactivateProviderCohort] = useReactivateProviderCohortMutation();

  // Get the active tab from storage when drawer opens
  useEffect(() => {
    if (isDrawerOpen('manageVolumeDrawer')) {
      const savedTab = storage.getItem(MANAGE_VOLUME_ACTIVE_TAB_KEY) as
        | 'PERSONAL'
        | 'COMPANY';
      if (savedTab) {
        setActiveTab(savedTab);
        // Clear after using
        storage.removeItem(MANAGE_VOLUME_ACTIVE_TAB_KEY);
      }
    }
  }, [isDrawerOpen]);

  // Custom drawer close handler that respects the lock
  const handleDrawerClose = () => {
    // Only close if not locked
    if (!drawerLockRef.current) {
      // Cancel any ongoing editing
      setEditingFolderId(null);
      setEditingFolderName('');

      dispatch({
        type: EncountersActionTypes.SET_EDITING_GROUP_ID,
        payload: { groupId: null },
      });
      dispatch({
        type: EncountersActionTypes.SET_EDITING_VOLUME_ID,
        payload: { volumeId: null },
      });
      dispatch({
        type: EncountersActionTypes.SET_EDITING_VOLUME_TYPE,
        payload: { volumeType: null },
      });
      closeDrawer('manageVolumeDrawer');
    }
  };

  const handleNewFolder = (type: 'PERSONAL' | 'COMPANY') => {
    setCreateFolderScope(type);
    setIsCreateFolderDialogOpen(true);
    // Set the lock when opening dialog
    drawerLockRef.current = true;
  };

  const handleCreateFolderSuccess = () => {
    // Refresh folder data
    mutateAccountFolders();
    // RTK Query will auto-invalidate provider cohorts cache
  };

  // Folder management functions
  const toggleFolder = (folderId: number) => {
    setOpenFolders((prev: { [key: number]: boolean }) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  // Folder editing handlers
  const handleEditFolder = (folder: AccountFolder) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.label);
  };

  const handleCancelEdit = () => {
    setEditingFolderId(null);
    setEditingFolderName('');
  };

  const handleSaveEdit = async () => {
    if (!editingFolderId || !editingFolderName.trim()) return;

    try {
      await updateAccountFolder(editingFolderId.toString(), {
        label: editingFolderName.trim(),
        scope: activeTab,
      });

      // Refresh folder data
      mutateAccountFolders();

      setNotification({
        title: 'Success',
        message: 'Folder name updated successfully',
        type: 'success',
      });

      // Reset editing state
      setEditingFolderId(null);
      setEditingFolderName('');
    } catch (error) {
      console.error('Error updating folder:', error);
      setNotification({
        title: 'Error',
        message: error?.error_message || 'Failed to update folder name',
        type: 'error',
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleDeleteFolder = (folder: AccountFolder) => {
    setFolderToDelete(folder);
    setIsDeleteFolderDialogOpen(true);
    // Set the lock when opening dialog
    drawerLockRef.current = true;
  };

  const handleDeleteFolderSuccess = () => {
    // Refresh folder data
    // RTK Query cache will be automatically refreshed when filter sets are modified
    mutateAccountFolders();
  };

  // Check if folder name has changed for enabled/disabled save button
  const isEditingNameChanged =
    editingFolderName.trim() !==
    (editingFolderId
      ? [...personalFolders, ...companyFolders].find(
          (f) => f.id === editingFolderId
        )?.label || ''
      : '');

  const handleEditVolume = (volume: VolumeItem) => {
    // AI searches have no edit drawer; they only support renaming.
    if (volume.volumeType === 'advancedSearch') {
      setVolumeToRename(volume);
      drawerLockRef.current = true;
      return;
    }

    const volumeId = volume.id.toString();

    // Set both ID and TYPE in context
    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_ID,
      payload: { volumeId },
    });

    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_TYPE,
      payload: { volumeType: volume.volumeType },
    });

    // Close the current drawer before opening the new one
    closeDrawer('manageVolumeDrawer');

    // Open based on local variable, not state
    if (volume.volumeType === 'filterSet') {
      openDrawer('editCodeSetsDrawer');
    } else {
      openDrawer('editEncountersDrawer');
    }
  };

  const handleDeleteClick = (volume: VolumeItem) => {
    setVolumeToDelete(volume);
    if (volume.volumeType === 'filterSet') {
      setIsDeleteFilterSetDialogOpen(true);
      // Set the lock when opening dialog
      drawerLockRef.current = true;
    } else if (volume.volumeType === 'advancedSearch') {
      setIsDeleteAISearchDialogOpen(true);
      drawerLockRef.current = true;
    } else {
      setIsDeleteEncounterDialogOpen(true);
      // Set the lock when opening dialog
      drawerLockRef.current = true;
    }
  };

  const handleDeleteSuccess = () => handleVolumeDeleted(volumeToDelete);

  // Reactivate a deactivated cohort; the mutation invalidates the list tag so
  // the row refreshes with its new (active) status automatically.
  const handleReactivate = async (volume: VolumeItem) => {
    try {
      await reactivateProviderCohort(volume.id.toString()).unwrap();
      setNotification({
        title: 'Success',
        message: `${volume.label} reactivated`,
        type: 'success',
      });
    } catch (error) {
      log.exception(error, { tags: { context: 'reactivate-cohort' } });
      setNotification({
        title: 'Error',
        message: 'Failed to reactivate code set',
        type: 'error',
      });
    }
  };

  // Render a status badge for a cohort row. Filter sets have no status, so this
  // returns null for anything other than the surfaced cohort states.
  const renderStatusBadge = (status?: string): React.ReactElement | null => {
    switch (status) {
      case 'PROCESSING':
        return (
          <Chip
            size="sm"
            variant="soft"
            color="neutral"
            startDecorator={
              <CircularProgress size="sm" aria-label="Processing" />
            }
            data-testid="cohort-status-processing"
          >
            Processing
          </Chip>
        );
      case 'ERROR':
        return (
          <Chip
            size="sm"
            variant="soft"
            color="danger"
            data-testid="cohort-status-error"
          >
            Error
          </Chip>
        );
      case 'DEACTIVATED':
        return (
          <Chip
            size="sm"
            variant="soft"
            color="neutral"
            data-testid="cohort-status-deactivated"
          >
            Deactivated
          </Chip>
        );
      default:
        return null;
    }
  };

  // Update the volume list rendering to handle folder context
  const renderVolumeList = (volumes: VolumeItem[], isInsideFolder = false) => {
    return volumes.map((volume) => (
      <Box
        key={`${volume.volumeType}-${volume.id}`}
        className={`flex items-center justify-between h-14 py-2 px-3 ${
          isInsideFolder
            ? 'pl-8 border-t border-neutral-200 first:border-t-0'
            : 'border border-neutral-200 rounded-md'
        } ${
          isDraggingRef.current ? 'hover:bg-sky-200' : 'hover:bg-neutral-50'
        }`}
        draggable={true}
        onDragStart={(e) => handleDragStart(e, volume)}
        onDragEnd={handleDragEnd}
        data-testid={`volume-item-${volume.id}`}
      >
        <Box className="flex flex-col">
          <Typography className="text-sm text-neutral-800 font-semibold">
            {volume.label}
          </Typography>
          <Typography className="text-xs text-neutral-500">
            {CODE_SET_KIND_SUBTITLES[volume.volumeType]}
          </Typography>
        </Box>

        <Box className="flex items-center gap-3" role="group" aria-label={`Actions for ${volume.label}`}>
          {volume.volumeType === 'cohort' && renderStatusBadge(volume.status)}
          {volume.volumeType === 'cohort' &&
            volume.status === 'DEACTIVATED' && (
              <Tooltip content="Reactivate" position="top">
                <Box
                  className="cursor-pointer"
                  onClick={() => handleReactivate(volume)}
                  data-testid={`reactivate-volume-${volume.id}`}
                  role="button"
                  aria-label={`Reactivate ${volume.label}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleReactivate(volume);
                    }
                  }}
                >
                  <Icon
                    icon={faArrowRotateLeft}
                    className="w-4 h-4 text-neutral-600"
                  />
                </Box>
              </Tooltip>
            )}
          <Box
            className="cursor-pointer"
            onClick={() => handleEditVolume(volume)}
            data-testid={`edit-volume-${volume.id}`}
            role="button"
            aria-label={`Edit ${volume.label}`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleEditVolume(volume);
              }
            }}
          >
            <Icon icon={faPencil} className="w-4 h-4 text-neutral-600" />
          </Box>
          <Box
            className="cursor-pointer"
            onClick={() => handleDeleteClick(volume)}
            data-testid={`delete-volume-${volume.id}`}
            role="button"
            aria-label={`Delete ${volume.label}`}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleDeleteClick(volume);
              }
            }}
          >
            <Icon icon={faTrashCanXmark} className="w-4 h-4 text-neutral-600" />
          </Box>
        </Box>
      </Box>
    ));
  };

  // Updated renderFolder function with inline editing
  const renderFolder = (folder: AccountFolder) => {
    const isOpen = openFolders[folder.id];
    const folderVolumes = getFolderVolumes(folder);
    const isEditing = editingFolderId === folder.id;

    return (
      <Box key={folder.id} className="mb-1" data-testid={`folder-container-${folder.id}`}>
        <Box
          className="border border-neutral-200 rounded-md overflow-hidden"
          onDragOver={(e) => {
            e.preventDefault();
            if (!isEditing) {
              e.currentTarget.classList.add('bg-sky-200');
            }
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove('bg-sky-200');
          }}
          onDrop={(e) => {
            // Keep the enclosing section's drop handler from also firing and
            // clearing the folder_id this drop just set.
            e.stopPropagation();
            e.currentTarget.classList.remove('bg-sky-200');
            if (!isEditing) {
              handleFolderDrop(e, folder);
            }
          }}
        >
          {/* Folder Header */}
          {isEditing ? (
            <Box
              className="flex items-center justify-between h-14 py-2 px-3 bg-neutral-50"
              data-testid={`folder-edit-mode-${folder.id}`}
            >
              <Box className="flex items-center flex-1">
                <Input
                  value={editingFolderName}
                  onChange={(e) => setEditingFolderName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  size="md"
                  variant="outlined"
                  className="flex-1 mx-2"
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  aria-label="Folder name"
                  data-testid={`folder-name-input-${folder.id}`}
                />
              </Box>

              <Box className="flex items-center gap-2" role="group" aria-label="Edit actions">
                <Box
                  className={`cursor-pointer ${
                    isEditingNameChanged
                      ? 'text-sky-600'
                      : 'text-neutral-300 opacity-50 cursor-not-allowed'
                  }`}
                  onClick={isEditingNameChanged ? handleSaveEdit : undefined}
                  data-testid={`save-edit-folder-${folder.id}`}
                  role="button"
                  aria-label="Save folder name"
                  aria-disabled={!isEditingNameChanged}
                  tabIndex={isEditingNameChanged ? 0 : -1}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && isEditingNameChanged) {
                      e.preventDefault();
                      handleSaveEdit();
                    }
                  }}
                >
                  <Icon
                    icon={faCheck}
                    className={`w-4 h-4 ${
                      isEditingNameChanged ? 'text-sky-600' : 'text-neutral-300'
                    }`}
                  />
                </Box>
                <Box
                  className="cursor-pointer"
                  onClick={handleCancelEdit}
                  data-testid={`cancel-edit-folder-${folder.id}`}
                  role="button"
                  aria-label="Cancel editing"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCancelEdit();
                    }
                  }}
                >
                  <Icon icon={faXmark} className="w-4 h-4 text-neutral-600" />
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              className="flex items-center justify-between h-14 py-2 px-3 hover:bg-neutral-50 cursor-pointer"
              onClick={() => toggleFolder(folder.id)}
              data-testid={`manage-folder-${folder.id}`}
              role="button"
              aria-expanded={isOpen}
              aria-label={`${folder.label} folder, ${isOpen ? 'expanded' : 'collapsed'}`}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleFolder(folder.id);
                }
              }}
            >
              <Box className="flex items-center">
                <Box className="w-4 h-4 flex items-center justify-center mr-2" aria-hidden="true">
                  <Icon
                    icon={isOpen ? faChevronDown : faChevronRight}
                    className="text-neutral-600"
                    size="sm"
                  />
                </Box>
                <Box className="w-4 h-4 flex items-center justify-center mr-2" aria-hidden="true">
                  <Icon
                    icon={isOpen ? faFolderOpen : faFolderBookmark}
                    className="text-neutral-600"
                  />
                </Box>
                <Typography className="text-sm font-semibold text-neutral-800">
                  {folder.label}
                </Typography>
              </Box>

              <Box className="flex items-center gap-3" role="group" aria-label={`Actions for ${folder.label}`}>
                <Box
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditFolder(folder);
                  }}
                  data-testid={`edit-folder-${folder.id}`}
                  role="button"
                  aria-label={`Edit ${folder.label} folder`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEditFolder(folder);
                    }
                  }}
                >
                  <Icon icon={faPencil} className="w-4 h-4 text-neutral-600" />
                </Box>
                <Box
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFolder(folder);
                  }}
                  data-testid={`delete-folder-${folder.id}`}
                  role="button"
                  aria-label={`Delete ${folder.label} folder`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteFolder(folder);
                    }
                  }}
                >
                  <Icon
                    icon={faTrashCanXmark}
                    className="w-4 h-4 text-neutral-600"
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Folder Contents - Integrated within same container */}
          {isOpen && folderVolumes.length > 0 && (
            <Box>{renderVolumeList(folderVolumes, true)}</Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Drawer
        isOpen={isDrawerOpen('manageVolumeDrawer')}
        onClose={() => {
          if (
            !isDeleteEncounterDialogOpen &&
            !isDeleteFilterSetDialogOpen &&
            !isDeleteAISearchDialogOpen &&
            !volumeToRename &&
            !isCreateFolderDialogOpen &&
            !isDeleteFolderDialogOpen
          ) {
            handleDrawerClose();
          }
        }}
        hideBackdrop
        size="base2"
        data-testid="manage-volume-drawer"
        anchor="right"
        variant="temporary"
      >
        <Box className="flex flex-col h-full relative">
          {/* Header */}
          <Drawer.Header className="bg-sky-100 text-primary border-b-0">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <p
                  className="font-bold text-lg"
                  data-testid="manage-codesets-drawer-title"
                >
                  Manage Code Sets
                </p>
              </div>
              <div className="flex items-center gap-2">
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={handleDrawerClose}
                  icon={faXmark}
                  className="text-primary hover:bg-sky-200"
                  data-testid="manage-codesets-drawer-close-button"
                />
              </div>
            </div>
          </Drawer.Header>

          {/* Content Area */}
          <Drawer.Content className="flex flex-col">
            {/* Tabs */}
            <Box
              className="flex border-b border-neutral-200 sticky top-0 bg-white"
              role="tablist"
              aria-label="Code set scope tabs"
            >
              <Tab
                isActive={activeTab === 'PERSONAL'}
                label="Personal"
                onClick={() => setActiveTab('PERSONAL')}
              />
              <Tab
                isActive={activeTab === 'COMPANY'}
                label="Company"
                onClick={() => setActiveTab('COMPANY')}
              />
            </Box>

            {/* Tab Content */}
            <Box className="p-6 flex-1">
              {activeTab === 'PERSONAL' ? (
                <Box className="flex flex-col">
                  {/* Folder Header Row */}
                  <Box className="flex items-center justify-between mb-4">
                    <Typography className="text-text-primary font-semibold">
                      Code Sets
                    </Typography>

                    <Box
                      className="flex items-center gap-1.5 cursor-pointer"
                      onClick={() => handleNewFolder('PERSONAL')}
                      data-testid="personal-new-folder-button"
                    >
                      <Icon
                        icon={faFolderPlus}
                        className="w-3 h-3 text-sky-900"
                      />
                      <Typography className="text-sm font-bold text-sky-900 pr-3">
                        New Folder
                      </Typography>
                    </Box>
                  </Box>

                  {/* Loading State */}
                  {isLoading && (
                    <Box
                      className="py-4 flex justify-center items-center"
                      data-testid="manage-volume-loading"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <Typography className="text-sm text-neutral-500">
                        Loading volumes...
                      </Typography>
                    </Box>
                  )}

                  {/* Error State */}
                  {hasError && (
                    <Box
                      className="py-4 flex justify-center items-center"
                      data-testid="manage-volume-error"
                      role="alert"
                    >
                      <Typography className="text-sm text-red-500">
                        Error loading volumes
                      </Typography>
                    </Box>
                  )}

                  {/* Personal Content */}
                  {!isLoading && !hasError && (
                    <Box
                      className={`flex flex-col gap-2 overflow-y-auto flex-1 ${
                        dragOverSection === 'personal' ? 'bg-sky-50' : ''
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOverSection('personal');
                        handleSectionDragOver(e, 'personal');
                      }}
                      onDragLeave={() => {
                        handleSectionDragLeave('personal');
                        setDragOverSection(null);
                      }}
                      onDrop={(e) => {
                        setDragOverSection(null);
                        handleSectionDrop(e, 'PERSONAL');
                      }}
                      data-testid="personal-section-drop-zone"
                      role="tabpanel"
                      aria-label="Personal code sets"
                    >
                      {/* Render Personal Folders */}
                      {personalFolders.map(renderFolder)}

                      {/* Render Personal Volumes not in folders */}
                      {personalVolumes.length > 0 && (
                        <Box className="flex flex-col gap-3">
                          {renderVolumeList(personalVolumes)}
                        </Box>
                      )}

                      {/* No content message */}
                      {personalFolders.length === 0 &&
                        personalVolumes.length === 0 && (
                          <Box
                            className="py-4 flex justify-center items-center"
                            data-testid="personal-empty-state"
                          >
                            <Typography className="text-sm text-neutral-500">
                              No personal volumes found
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box className="flex flex-col">
                  {/* Folder Header Row */}
                  <Box className="flex items-center justify-between mb-4">
                    <Typography className="text-text-primary font-semibold">
                      Volumes
                    </Typography>

                    <Box
                      className="flex items-center gap-1.5 cursor-pointer"
                      onClick={() => handleNewFolder('COMPANY')}
                      data-testid="company-new-folder-button"
                    >
                      <Icon
                        icon={faFolderPlus}
                        className="w-3 h-3 text-sky-900"
                      />
                      <Typography className="text-sm font-bold text-sky-900 pr-3">
                        New Folder
                      </Typography>
                    </Box>
                  </Box>

                  {/* Loading State */}
                  {isLoading && (
                    <Box
                      className="py-4 flex justify-center items-center"
                      data-testid="manage-volume-loading"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <Typography className="text-sm text-neutral-500">
                        Loading volumes...
                      </Typography>
                    </Box>
                  )}

                  {/* Error State */}
                  {hasError && (
                    <Box
                      className="py-4 flex justify-center items-center"
                      data-testid="manage-volume-error"
                      role="alert"
                    >
                      <Typography className="text-sm text-red-500">
                        Error loading volumes
                      </Typography>
                    </Box>
                  )}

                  {/* Company Content */}
                  {!isLoading && !hasError && (
                    <Box
                      className={`flex flex-col gap-2 overflow-y-auto flex-1 ${
                        dragOverSection === 'company' ? 'bg-sky-50' : ''
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOverSection('company');
                        handleSectionDragOver(e, 'company');
                      }}
                      onDragLeave={() => {
                        handleSectionDragLeave('company');
                        setDragOverSection(null);
                      }}
                      onDrop={(e) => {
                        setDragOverSection(null);
                        handleSectionDrop(e, 'COMPANY');
                      }}
                      data-testid="company-section-drop-zone"
                      role="tabpanel"
                      aria-label="Company code sets"
                    >
                      {/* Render Company Folders */}
                      {companyFolders.map(renderFolder)}

                      {/* Render Company Volumes not in folders */}
                      {companyVolumes.length > 0 && (
                        <Box className="flex flex-col gap-3">
                          {renderVolumeList(companyVolumes)}
                        </Box>
                      )}

                      {/* No content message */}
                      {companyFolders.length === 0 &&
                        companyVolumes.length === 0 && (
                          <Box
                            className="py-4 flex justify-center items-center"
                            data-testid="company-empty-state"
                          >
                            <Typography className="text-sm text-neutral-500">
                              No company volumes found
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Drawer.Content>

          {/* Footer */}
          <Drawer.Footer>
            <Box className="flex w-full justify-end gap-2">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDrawerClose}
                className="!rounded-full w-full"
                data-testid="manage-volume-done-button"
              >
                Done
              </Button>
            </Box>
          </Drawer.Footer>
        </Box>
      </Drawer>

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        isOpen={isCreateFolderDialogOpen}
        onClose={() => {
          setIsCreateFolderDialogOpen(false);
          setCreateFolderScope(null);
          // Release the lock after dialog closes
          setTimeout(() => {
            drawerLockRef.current = false;
          }, 100);
        }}
        scope={createFolderScope}
        onCreateSuccess={handleCreateFolderSuccess}
      />

      {/* Encounter Delete Dialog */}
      <DeleteEncounterDialog
        isOpen={isDeleteEncounterDialogOpen}
        onClose={() => {
          setIsDeleteEncounterDialogOpen(false);
          setVolumeToDelete(null);
          // Release the lock after dialog closes
          setTimeout(() => {
            drawerLockRef.current = false;
          }, 100);
        }}
        volumeToDelete={
          volumeToDelete && volumeToDelete.volumeType === 'cohort'
            ? {
                id: volumeToDelete.id,
                label: volumeToDelete.label,
              }
            : null
        }
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* Code Set Delete Dialog */}
      <DeleteCodeSetDialog
        isOpen={isDeleteFilterSetDialogOpen}
        onClose={() => {
          setIsDeleteFilterSetDialogOpen(false);
          setVolumeToDelete(null);
          // Release the lock after dialog closes
          setTimeout(() => {
            drawerLockRef.current = false;
          }, 100);
        }}
        volumeToDelete={
          volumeToDelete && volumeToDelete.volumeType === 'filterSet'
            ? {
                id: volumeToDelete.id,
                label: volumeToDelete.label,
                volumeType: 'filterSet',
              }
            : null
        }
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* AI Search Delete Dialog */}
      <DeleteAISearchDialog
        isOpen={isDeleteAISearchDialogOpen}
        onClose={() => {
          setIsDeleteAISearchDialogOpen(false);
          setVolumeToDelete(null);
          // Release the lock after dialog closes
          setTimeout(() => {
            drawerLockRef.current = false;
          }, 100);
        }}
        volumeToDelete={
          volumeToDelete && volumeToDelete.volumeType === 'advancedSearch'
            ? {
                id: volumeToDelete.id,
                label: volumeToDelete.label,
              }
            : null
        }
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* AI Search Rename Dialog */}
      {volumeToRename && (
        <RenameAISearchDialog
          isOpen
          onClose={() => {
            setVolumeToRename(null);
            // Release the lock after dialog closes
            setTimeout(() => {
              drawerLockRef.current = false;
            }, 100);
          }}
          volumeToRename={{
            id: volumeToRename.id,
            label: volumeToRename.label,
          }}
          onRenameSuccess={renameAppliedAdvancedSearch}
        />
      )}

      {/* Delete Folder Dialog */}
      <DeleteFolderDialog
        isOpen={isDeleteFolderDialogOpen}
        onClose={() => {
          setIsDeleteFolderDialogOpen(false);
          setFolderToDelete(null);
          // Release the lock when closing dialog
          drawerLockRef.current = false;
        }}
        folderToDelete={folderToDelete}
        onDeleteSuccess={handleDeleteFolderSuccess}
      />
    </>
  );
};

export default ManageVolumeDrawer;
